import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, RefreshCw, Copy, DollarSign, Check, ExternalLink, Pencil, XCircle, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DbEvent, Contract, CleaningChecklist, Payment, PaymentMethod } from '../types';
import {
  calcBase, calcTotal, calcBalance, isOverdue, calcLateInterest,
  fmtMoney, fmtDate, fmtTime, CLEANING_FEE_AMOUNT, SECURITY_FEE_AMOUNT, OVERAGE_RATE,
  INSURANCE_WAIVER_FEE,
} from '../lib/pricing';
import { sendContractEmail, sendChecklistEmail } from '../lib/email';

const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;

function Badge({ label, color }: { label: string; color: string }) {
  return <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${color}`}>{label}</span>;
}

function statusColor(s: string) {
  return ({ upcoming: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500' }[s] ?? 'bg-gray-100 text-gray-500');
}
function contractColor(s: string) {
  return ({ draft: 'bg-gray-100 text-gray-600', sent: 'bg-yellow-100 text-yellow-700', signed: 'bg-green-100 text-green-700' }[s] ?? 'bg-gray-100 text-gray-600');
}
function checklistColor(s: string) {
  return ({ pending: 'bg-gray-100 text-gray-600', submitted: 'bg-green-100 text-green-700', incomplete: 'bg-red-100 text-red-600' }[s] ?? 'bg-gray-100 text-gray-600');
}

function AuditRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-800 text-right break-all">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h2 className="font-semibold text-gray-800 text-sm">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<DbEvent | null>(null);
  const [contract, setContract] = useState<Contract | null>(null);
  const [checklist, setChecklist] = useState<CleaningChecklist | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  // Payment form
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('zelle');
  const [payNotes, setPayNotes] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // Post-event fields
  const [actualRevenue, setActualRevenue] = useState('');
  const [overageHours, setOverageHours] = useState('');
  const [damageFee, setDamageFee] = useState('');
  const [chargeClean, setChargeClean] = useState(false);
  const [chargeSecurity, setChargeSecurity] = useState(false);
  const [postSaving, setPostSaving] = useState(false);

  // UI state
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const [evRes, conRes, clRes, payRes] = await Promise.all([
      supabase.from('events').select('*, clients(*)').eq('id', id).single(),
      supabase.from('contracts').select('*').eq('event_id', id).single(),
      supabase.from('cleaning_checklist').select('*').eq('event_id', id).single(),
      supabase.from('payments').select('*').eq('event_id', id).order('paid_at', { ascending: false }),
    ]);
    setEvent(evRes.data);
    setContract(conRes.data ?? null);
    setChecklist(clRes.data ?? null);
    setPayments(payRes.data ?? []);
    if (evRes.data) {
      setActualRevenue(String(evRes.data.actual_revenue ?? ''));
      setOverageHours(String(evRes.data.overage_hours ?? ''));
      setDamageFee(String(evRes.data.damage_fee ?? ''));
      setChargeClean((evRes.data.cleaning_fee ?? 0) > 0);
      setChargeSecurity((evRes.data.security_fee ?? 0) > 0);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const logPayment = async () => {
    if (!event || !payAmount) return;
    setPayLoading(true);
    const amount = parseFloat(payAmount);
    await supabase.from('payments').insert({ event_id: event.id, amount, method: payMethod, notes: payNotes || null });
    const newPaid = (event.amount_paid ?? 0) + amount;
    await supabase.from('events').update({ amount_paid: newPaid }).eq('id', event.id);
    setPayAmount(''); setPayNotes('');
    setPayLoading(false);
    load();
  };

  const savePostEvent = async () => {
    if (!event) return;
    setPostSaving(true);
    const overageHrs = parseFloat(overageHours) || 0;
    const updates: Partial<DbEvent> = {
      actual_revenue: actualRevenue ? parseFloat(actualRevenue) : null,
      overage_hours: overageHrs,
      overage_fee: overageHrs * OVERAGE_RATE,
      damage_fee: parseFloat(damageFee) || 0,
      cleaning_fee: chargeClean ? CLEANING_FEE_AMOUNT : 0,
      security_fee: chargeSecurity ? SECURITY_FEE_AMOUNT : 0,
    };
    await supabase.from('events').update(updates).eq('id', event.id);
    setPostSaving(false);
    load();
  };

  const markCompleted = async () => {
    if (!event) return;
    await supabase.from('events').update({ status: 'completed' }).eq('id', event.id);
    load();
  };

  const sendContract = async () => {
    if (!event || !contract) return;
    const clientEmail = event.clients?.email;
    if (!clientEmail) { setEmailError('Client has no email address on file.'); return; }
    setEmailSending(true); setEmailError('');
    try {
      await sendContractEmail(clientEmail, event.clients!.name, fmtDate(event.event_date), contract.signing_token);
      await supabase.from('contracts').update({ contract_status: 'sent', sent_at: new Date().toISOString() }).eq('id', contract.id);
      load();
    } catch (e: any) {
      setEmailError(e.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const sendChecklist = async () => {
    if (!event || !checklist) return;
    const clientEmail = event.clients?.email;
    if (!clientEmail) { setEmailError('Client has no email address on file.'); return; }
    setEmailSending(true); setEmailError('');
    try {
      await sendChecklistEmail(clientEmail, event.clients!.name, checklist.token);
      load();
    } catch (e: any) {
      setEmailError(e.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const cancelEvent = async () => {
    if (!window.confirm('Cancel this event? This will mark it as cancelled. You can undo this by editing the event status.')) return;
    await supabase.from('events').update({ status: 'cancelled' }).eq('id', id!);
    load();
  };

  const copyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex justify-center items-center py-24">
      <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!event) return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-center text-gray-500">Event not found.</div>
  );

  const total = calcTotal(event);
  const balance = calcBalance(event);
  const overdue = isOverdue(event);
  const lateInterest = overdue ? calcLateInterest(event) : 0;
  const isPast = new Date(event.event_date + 'T23:59:59') < new Date();
  const signingLink = `${APP_URL}/sign/${contract?.signing_token ?? ''}`;
  const checklistLink = `${APP_URL}/checklist/${checklist?.token ?? ''}`;

  const feeRows = [
    { label: event.pricing_model === 'flat_rate' ? 'Flat Rate' : 'Revenue Share Base', amount: calcBase(event) },
    ...(event.restroom_fee > 0 ? [{ label: 'Restroom Fee', amount: event.restroom_fee }] : []),
    ...(event.insurance_fee > 0 ? [{ label: 'Insurance Waiver', amount: event.insurance_fee }] : []),
    ...(event.cleaning_fee > 0 ? [{ label: 'Cleaning Fee', amount: event.cleaning_fee }] : []),
    ...(event.security_fee > 0 ? [{ label: 'Security Fee', amount: event.security_fee }] : []),
    ...(event.overage_fee > 0 ? [{ label: `Overage (${event.overage_hours}h)`, amount: event.overage_fee }] : []),
    ...(event.damage_fee > 0 ? [{ label: 'Damage Fee', amount: event.damage_fee }] : []),
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h1 className="font-serif text-xl font-bold text-gray-900">{event.clients?.name ?? '—'}</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge label={event.status} color={statusColor(event.status)} />
            <button
              onClick={() => navigate(`/events/${id}/edit`)}
              className="flex items-center gap-1 text-xs text-gray-500 border border-gray-200 hover:bg-gray-50 px-2 py-1.5 rounded-lg transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
            {event.status !== 'cancelled' && (
              <button
                onClick={cancelEvent}
                className="flex items-center gap-1 text-xs text-red-500 border border-red-200 hover:bg-red-50 px-2 py-1.5 rounded-lg transition-colors"
              >
                <XCircle size={12} /> Cancel
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">{event.event_type} · {fmtDate(event.event_date)} · {fmtTime(event.start_time)} – 12:30 AM</p>
        {event.estimated_attendance && (
          <p className="text-sm text-gray-500">~{event.estimated_attendance} guests</p>
        )}
        {event.notes && <p className="text-sm text-gray-600 mt-2 italic">"{event.notes}"</p>}
      </div>

      {/* Fee Breakdown */}
      <Section title="Fee Breakdown">
        <div className="space-y-1">
          {feeRows.map(row => (
            <div key={row.label} className="flex justify-between text-sm py-1">
              <span className="text-gray-600">{row.label}</span>
              <span className="font-medium text-gray-900">{fmtMoney(row.amount)}</span>
            </div>
          ))}
          <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
            <div className="flex justify-between text-sm font-bold">
              <span>Total Owed</span><span className="text-gold-700">{fmtMoney(total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Amount Paid</span>
              <span className="text-green-600 font-medium">{fmtMoney(event.amount_paid ?? 0)}</span>
            </div>
            <div className="flex justify-between text-sm font-bold">
              <span className={overdue ? 'text-red-600' : ''}>Balance</span>
              <span className={overdue ? 'text-red-600' : balance > 0 ? 'text-gray-900' : 'text-green-600'}>
                {fmtMoney(balance)}
              </span>
            </div>
            {event.payment_due_date && (
              <div className="flex justify-between text-xs text-gray-400 pt-1">
                <span>Due Date</span><span>{fmtDate(event.payment_due_date)}</span>
              </div>
            )}
          </div>
        </div>
        {overdue && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
            <strong>OVERDUE</strong> — Late interest: {fmtMoney(lateInterest)} (10% p.a. · display only)
          </div>
        )}
      </Section>

      {/* Contract */}
      <Section title="Contract">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600">Status:</span>
          <Badge label={contract?.contract_status ?? 'no contract'} color={contractColor(contract?.contract_status ?? '')} />
        </div>

        {emailError && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{emailError}</div>
        )}

        {(!contract || contract.contract_status === 'draft') && (
          <button
            onClick={sendContract}
            disabled={emailSending}
            className="flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Send size={14} /> {emailSending ? 'Sending…' : 'Generate & Send Contract'}
          </button>
        )}

        {contract?.contract_status === 'sent' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Sent {contract.sent_at ? fmtDate(contract.sent_at.split('T')[0]) : '—'}</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={sendContract}
                disabled={emailSending}
                className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} /> {emailSending ? 'Sending…' : 'Resend'}
              </button>
              <button
                onClick={() => copyLink(signingLink)}
                className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
              >
                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <a
                href={signingLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
              >
                <ExternalLink size={12} /> Preview
              </a>
            </div>
          </div>
        )}

        {contract?.contract_status === 'signed' && (
          <div className="space-y-3">
            <p className="text-sm text-green-700 font-medium">Signed by {contract.client_name_typed}</p>
            <p className="text-xs text-gray-500">
              {contract.signed_at ? `Signed on ${fmtDate(contract.signed_at.split('T')[0])}` : ''}
            </p>
            {contract.signature_data && (
              <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden bg-white">
                <p className="text-xs text-gray-400 px-2 pt-1">Signature on file</p>
                <img src={contract.signature_data} alt="Signature" className="max-h-20 p-2" />
              </div>
            )}

            <a
              href={`/api/contract-pdf/${contract.signing_token}`}
              className="inline-flex items-center gap-1.5 bg-gold-500 hover:bg-gold-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Download size={14} /> Download Signed PDF
            </a>

            {contract.contract_snapshot ? (
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Signed Record</p>
                <AuditRow label="Terms version" value={contract.terms_version ?? '—'} />
                <AuditRow label="Agreement accepted" value={contract.agreed_to_terms ? 'Yes' : 'No'} />
                <AuditRow label="IP address" value={contract.signed_ip ?? '—'} />
                <AuditRow label="Device / browser" value={contract.signed_user_agent ?? '—'} />
                <p className="text-xs text-gray-400 pt-1">
                  This is a locked snapshot of the contract exactly as it was signed. Editing the event will not change it.
                </p>
              </div>
            ) : (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                This contract was signed before snapshot record-keeping was enabled, so no locked PDF/audit trail is available for it.
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Next Steps (after contract sent) */}
      {contract && contract.contract_status !== 'draft' && (
        <Section title="Next Steps for Client">
          <div className="text-sm text-gray-700 space-y-2">
            <p>Hey {event.clients?.name?.split(' ')[0]}! Here's what you need to have ready before your event:</p>
            <ol className="list-decimal ml-4 space-y-1 text-gray-600">
              <li>Get at least 1 portable restroom for your guests</li>
              <li>Have at least 1 security guard present who is 21 or older</li>
              <li>For insurance — either get a one-day event liability policy naming <strong>Nuno Productions LLC</strong> as additional insured, OR send {fmtMoney(INSURANCE_WAIVER_FEE)} via Zelle and I'll get it handled</li>
            </ol>
            <p className="text-gray-500">See you on {fmtDate(event.event_date)}!</p>
          </div>
        </Section>
      )}

      {/* Payments */}
      <Section title="Payments">
        {payments.length > 0 && (
          <div className="mb-4 divide-y divide-gray-100">
            {payments.map(p => (
              <div key={p.id} className="flex justify-between items-center py-2 text-sm">
                <div>
                  <span className="font-medium text-gray-900">{fmtMoney(p.amount)}</span>
                  <span className="text-gray-400 ml-2 capitalize">{p.method}</span>
                </div>
                <span className="text-gray-400 text-xs">{p.paid_at ? fmtDate(p.paid_at.split('T')[0]) : ''}</span>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-semibold text-gray-600 mb-2">Log Payment</p>
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <input
                type="number"
                value={payAmount}
                onChange={e => setPayAmount(e.target.value)}
                placeholder="Amount"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>
            <select
              value={payMethod}
              onChange={e => setPayMethod(e.target.value as PaymentMethod)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 bg-white"
            >
              {['zelle','cash','venmo','cashapp','other'].map(m => (
                <option key={m} value={m} className="capitalize">{m.charAt(0).toUpperCase() + m.slice(1)}</option>
              ))}
            </select>
          </div>
          <input
            value={payNotes}
            onChange={e => setPayNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 mb-2"
          />
          <button
            onClick={logPayment}
            disabled={!payAmount || payLoading}
            className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
          >
            <DollarSign size={14} /> {payLoading ? 'Saving…' : 'Log Payment'}
          </button>
        </div>
      </Section>

      {/* Post-Event Section (only after event date) */}
      {isPast && (
        <Section title="Post-Event">
          <div className="space-y-4">
            {event.pricing_model === 'revenue_share' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Actual Revenue Collected ($)</label>
                <input
                  type="number"
                  value={actualRevenue}
                  onChange={e => setActualRevenue(e.target.value)}
                  placeholder="e.g. 2500"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                />
                {actualRevenue && (
                  <p className="text-xs text-gray-500 mt-1">
                    Revenue share total: {fmtMoney(calcBase({ pricing_model: 'revenue_share', actual_revenue: parseFloat(actualRevenue) }))}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Overage Hours past 12:30 AM</label>
              <input
                type="number"
                step="0.5"
                value={overageHours}
                onChange={e => setOverageHours(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
              {overageHours && parseFloat(overageHours) > 0 && (
                <p className="text-xs text-gray-500 mt-1">Fee: {fmtMoney(parseFloat(overageHours) * OVERAGE_RATE)}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Damage Fee ($)</label>
              <input
                type="number"
                value={damageFee}
                onChange={e => setDamageFee(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">Charge Cleaning Fee</p>
                <p className="text-xs text-gray-400">{fmtMoney(CLEANING_FEE_AMOUNT)} — if checklist incomplete</p>
              </div>
              <button
                onClick={() => setChargeClean(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${chargeClean ? 'bg-gold-500' : 'bg-gray-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${chargeClean ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">Charge Security Fee</p>
                <p className="text-xs text-gray-400">{fmtMoney(SECURITY_FEE_AMOUNT)} — if no 21+ guard present</p>
              </div>
              <button
                onClick={() => setChargeSecurity(v => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${chargeSecurity ? 'bg-gold-500' : 'bg-gray-200'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${chargeSecurity ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={savePostEvent}
                disabled={postSaving}
                className="flex-1 bg-gray-900 hover:bg-gray-800 text-white py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {postSaving ? 'Saving…' : 'Save Post-Event Data'}
              </button>
              {event.status !== 'completed' && (
                <button
                  onClick={markCompleted}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <Check size={14} /> Mark Complete
                </button>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Cleaning Checklist */}
      <Section title="Cleaning Checklist">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-600">Status:</span>
          <Badge label={checklist?.status ?? 'no checklist'} color={checklistColor(checklist?.status ?? '')} />
        </div>

        {checklist?.status === 'submitted' && (
          <div className="mb-3 text-sm text-gray-700">
            <p>Submitted by <strong>{checklist.completed_by}</strong></p>
            {checklist.submitted_at && <p className="text-xs text-gray-400">{fmtDate(checklist.submitted_at.split('T')[0])}</p>}
            {checklist.cleaning_fee_triggered && (
              <p className="text-xs text-red-600 mt-1">⚠️ Cleaning fee was triggered</p>
            )}
          </div>
        )}

        {emailError && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{emailError}</div>
        )}

        <div className="space-y-2">
          <button
            onClick={sendChecklist}
            disabled={emailSending}
            className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Send size={14} /> {emailSending ? 'Sending…' : 'Send Checklist to Client'}
          </button>

          {checklist && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => copyLink(checklistLink)}
                className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition-colors"
              >
                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                Copy Link
              </button>
              <a
                href={checklistLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 transition-colors"
              >
                <ExternalLink size={12} /> Preview
              </a>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
